import { useFormik } from 'formik';
import { z } from 'zod';
import { Config } from '@/types';
import { isValidPhoneNumber } from '@/utils/phone-utils';
import { createFieldContextHelpers } from '@/utils/field-context-helpers';
import { useRef, useEffect } from 'react';
import type { CountryCode } from '@/utils/location-detection';
import { DATE_FIELD_NOT_SURE_VALUE } from '@/components/ui/FormElements/Date';

/**
 * Builds a Zod-based validate function for Formik.
 * Replaces previous Yup schema with equivalent rules (required + dependency, type validation).
 */
function createValidateFn(
  fields: Config.Field[],
  userCountryCode?: CountryCode
): (values: Record<string, string>) => Record<string, string> {
  const enabledFields = fields.filter(f => f.enabled !== false);
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of enabledFields) {
    shape[field.name] = z.string().optional();
  }
  const baseSchema = z.object(shape);

  const schemaWithRefinements = baseSchema.superRefine((data, ctx) => {
    for (const field of enabledFields) {
      const value = data[field.name];
      const trimmed = value != null ? String(value).trim() : '';

      // Required (with optional dependency)
      if (field.required) {
        let requireField = true;
        if (field.dependsOn) {
          const dependentValue = data[field.dependsOn.name];
          requireField = dependentValue === field.dependsOn.value;
        }
        if (requireField && (value == null || value === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${field.title} is required`,
            path: [field.name],
          });
          continue;
        }
      }

      if (value == null || trimmed === '') continue;

      // Regex override
      if (field.validation?.regex) {
        const re = new RegExp(field.validation.regex.pattern);
        if (!re.test(trimmed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: field.validation.regex.message,
            path: [field.name],
          });
        }
        continue;
      }

      const typeToValidate = field.validation?.validationType ?? field.type;

      switch (typeToValidate) {
        case 'number':
          if (!/^\d+$/.test(trimmed)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field.title} must be a valid number`,
              path: [field.name],
            });
          }
          break;
        case 'email': {
          const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRe.test(trimmed)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field.title} must be a valid email address`,
              path: [field.name],
            });
          }
          break;
        }
        case 'tel': {
          try {
            if (!isValidPhoneNumber(trimmed)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field.title} must be a valid phone number`,
                path: [field.name],
              });
            }
          } catch {
            if (!/^[+]?[1-9]\d{0,15}$/.test(trimmed)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field.title} must be a valid phone number`,
                path: [field.name],
              });
            }
          }
          break;
        }
        case 'select':
        case 'radio': {
          let optionsToValidate: string[] = [];
          if (field.dynamicOptions) {
            try {
              const helpers = createFieldContextHelpers(userCountryCode);
              const evaluated = field.dynamicOptions(
                data as Record<string, string>,
                helpers
              );
              optionsToValidate =
                Array.isArray(evaluated) && evaluated.length > 0
                  ? (evaluated as string[])
                  : ((field.options as string[]) ?? []);
            } catch {
              optionsToValidate = (field.options as string[]) ?? [];
            }
          } else if (
            field.options?.length &&
            typeof field.options[0] === 'string'
          ) {
            optionsToValidate = field.options as string[];
          }
          if (
            optionsToValidate.length > 0 &&
            !optionsToValidate.includes(trimmed)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field.title} must be one of the available options`,
              path: [field.name],
            });
          }
          break;
        }
        case 'range-slider': {
          try {
            const parsed = JSON.parse(trimmed);
            const valid =
              typeof parsed === 'object' &&
              typeof parsed.min === 'number' &&
              typeof parsed.max === 'number' &&
              parsed.min <= parsed.max;
            if (!valid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${field.title} must be a valid range`,
                path: [field.name],
              });
            }
          } catch {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field.title} must be a valid range`,
              path: [field.name],
            });
          }
          break;
        }
        case 'date': {
          if (
            trimmed !== DATE_FIELD_NOT_SURE_VALUE &&
            !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field.title} must be a valid date in format YYYY-MM-DD`,
              path: [field.name],
            });
          }
          break;
        }
        default:
          break;
      }
    }
  });

  return (values: Record<string, string>) => {
    const result = schemaWithRefinements.safeParse(values);

    if (result.success) return {};
    const formErrors: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.');
      if (path && !formErrors[path]) {
        formErrors[path] = issue.message;
      }
    }
    return formErrors;
  };
}

interface Props {
  fields: Config.Field[];
  initialValues?: Record<string, string>;
  onChange?: () => void;
  onSubmit: (values: Record<string, string>) => void;
  userCountryCode?: CountryCode;
}

export const useDynamicFieldsForm = ({
  fields,
  initialValues = {},
  onChange,
  onSubmit,
  userCountryCode,
}: Props) => {
  const fieldOptionsRef = useRef<Record<string, string[] | [number, number][]>>(
    {}
  );
  const formikValuesRef = useRef<Record<string, string>>(initialValues);

  const formik = useFormik<Record<string, string>>({
    initialValues,
    validate: createValidateFn(fields, userCountryCode),
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    formikValuesRef.current = formik.values;
  }, [formik.values]);

  const handleOnChange = (name: string, value: string) => {
    formik.setFieldValue(name, value);

    // Only re-validate while typing when the field is already touched AND has
    // an error — i.e. the user is actively fixing a mistake. Don't call
    // setFieldTouched here: marking touched on every keystroke causes errors
    // pre-populated by a previous blur to appear on the very first character.
    if (formik.touched[name] && formik.errors[name]) {
      setTimeout(() => {
        const currentValues = { ...formikValuesRef.current, [name]: value };
        const errs = createValidateFn(fields, userCountryCode)(currentValues);
        formik.setFieldError(name, errs[name]);
      }, 0);
    }

    onChange?.();
  };

  const handleOnBlur = async (name: string) => {
    formik.setFieldTouched(name, true, false);

    // formik.validateField() is a no-op when using the form-level `validate`
    // prop (it only works with per-field or validationSchema validators).
    // formik.validateForm() would pre-populate errors for ALL fields, causing
    // untouched fields to show errors on the first keystroke (handleOnChange
    // marks touched=true which then reveals the pre-populated error).
    // Instead, run the validate function ourselves and only update this
    // field's error via setFieldError.
    await new Promise(resolve => setTimeout(resolve, 0));
    const errs = createValidateFn(
      fields,
      userCountryCode
    )(formikValuesRef.current);
    formik.setFieldError(name, errs[name]);

    const fieldContextHelpers = createFieldContextHelpers(userCountryCode);
    fields.forEach(field => {
      if (field.dynamicOptions && field.enabled !== false) {
        try {
          const evaluated = field.dynamicOptions(
            formik.values,
            fieldContextHelpers
          );
          const currentOptions =
            Array.isArray(evaluated) && evaluated.length > 0
              ? evaluated
              : field.options || [];

          const previousOptions =
            fieldOptionsRef.current[field.name] || field.options || [];

          const optionsChanged =
            JSON.stringify(currentOptions) !== JSON.stringify(previousOptions);

          if (optionsChanged) {
            fieldOptionsRef.current[field.name] = currentOptions;

            const currentValue = formik.values[field.name];
            if (currentValue) {
              if (
                typeof currentOptions[0] === 'string' &&
                !(currentOptions as string[]).includes(currentValue)
              ) {
                formik.setFieldValue(field.name, '');
              }
            }
          }
        } catch (error) {
          console.warn(
            `Error evaluating dynamicOptions for field ${field.name}:`,
            error
          );
        }
      }
    });
  };

  return {
    formik,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onSubmit: formik.submitForm,
  };
};
